/**
 * GTM(Google Tag Manager) 연동
 *
 * 배경 — CRM 팀 요청으로 그룹 공용 컨테이너(GTM-NTC6M9JB)를 붙인다.
 * 컨테이너 로드는 public/index.html 의 스니펫이 하고, 이 파일은 스니펫만으로는
 * 잡히지 않는 것들을 dataLayer 로 밀어 넣는다.
 *
 *   1) SPA 페이지뷰 — createWebHistory 라우터라 화면을 옮겨도 문서가 다시 로드되지 않는다.
 *      GTM 기본 페이지뷰는 최초 1회뿐이라 그대로 두면 전 화면이 "/" 하나로 집계된다.
 *   2) 앱/웹 구분 — 같은 컨테이너를 그룹 홈페이지들과 공유하므로, CRM 쪽에서
 *      앱 웹뷰 트래픽을 분리할 수 있어야 한다.
 *   3) 로그인 사용자(userInfo) · 멤버십 결제 완료(purchase) — 2026-09-30 CRM 팀 추가 요청.
 *
 * 이 파일은 다른 모듈에 의존하지 않는다. GTM 은 기능 브랜치와 무관하게 붙고 떼야 하므로
 * 브랜치마다 존재 여부가 갈리는 파일(utils/appVersion.js 등)을 import 하지 않는다.
 */

/** CRM 팀 공용 컨테이너 ID. 그룹 홈페이지(parnashotel.com 등)와 같은 값이다. */
export const GTM_ID = "GTM-NTC6M9JB"

/**
 * page_location 에서 지워야 하는 쿼리 파라미터.
 *
 * 결제 복귀 화면이 payment-id, result-message 를 쿼리로 달고 들어온다
 * (서버 PaymentService.buildReturnHtml). 그대로 두면 결제 식별자가 GA 에 그대로 쌓인다.
 */
const STRIP_PARAMS = ["payment-id", "result-message", "token", "accessToken", "refreshToken"]

function push(payload) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(payload)
}

/**
 * 앱 웹뷰인지 판별한다.
 *
 * UA 만으로는 앱과 모바일 브라우저가 구분되지 않는다(커스텀 UA 마커 이전 버전 때문).
 * 네이티브가 주입하는 브릿지의 존재 여부로 판별한다.
 * 서버 AppClientUtil / utils/appVersion.js 와 같은 판정 규칙이다.
 *
 * @returns {{isApp: Boolean, platform: String}} platform 은 "AOS" | "IOS" | "WEB"
 */
export function getClientInfo() {
    const ua = navigator.userAgent

    const isApp = !!(
        (window.ParnasApp && window.ParnasApp.login) ||
        (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.login)
    )

    const platform = /iPhone|iPad|iPod/i.test(ua) ? "IOS"
        : /Android/i.test(ua) ? "AOS"
            : "WEB"

    return { isApp, platform }
}

/** 결제 식별자 등을 걷어낸 절대 URL */
function cleanLocation(fullPath) {
    const url = new URL(fullPath, window.location.origin)
    STRIP_PARAMS.forEach((k) => url.searchParams.delete(k))
    return url.toString()
}

/**
 * 컨테이너가 첫 태그를 쏘기 전에 클라이언트 정보를 올려둔다.
 * app.mount() 보다 먼저 호출해야 최초 페이지뷰에도 값이 실린다.
 */
export function initGtm() {
    const { isApp, platform } = getClientInfo()
    push({ is_app: isApp, client_platform: platform })
}

/**
 * 라우트 이동을 가상 페이지뷰로 올린다.
 *
 * 최초 진입은 올리지 않는다. 문서가 실제로 로드되는 시점이라 컨테이너의 Google 태그가
 * 이미 자동으로 페이지뷰를 보내며, 여기서 또 올리면 첫 화면만 두 번 집계된다.
 *
 * 이벤트명을 page_view 가 아니라 virtual_page_view 로 둔 것도 같은 이유다.
 * GTM 콘솔에서 자동 페이지뷰와 구분되어야 트리거를 따로 걸 수 있다.
 */
let firstNavigation = true

export function sendPageView(to) {
    if (firstNavigation) {
        firstNavigation = false
        return
    }

    push({
        event: "virtual_page_view",
        page_path: to.path,
        page_location: cleanLocation(to.fullPath),
        page_title: document.title,
    })
}

let lastLoginIdHash = null

/**
 * 로그인 사용자 식별값을 올린다 (CRM 요청: LMS 발송 → 앱 진입 → 결제 퍼널을 사람 단위로 잇는다).
 *
 * event 없는 push 라 그 자체로는 아무것도 전송되지 않는다. GTM 데이터 모델에 남아 있다가
 * 이후 이벤트(페이지뷰·purchase 등)가 날 때 태그가 함께 읽어 간다.
 *
 * - 원문 아이디는 보내지 않는다. 서버가 내려준 해시(/members/me 의 loginIdHash)만 쓴다.
 *   해싱 규칙은 서버 GaUtil 에 있다. http 인 개발계에서는 브라우저 crypto.subtle 을 쓸 수 없어 서버에서 만든다.
 * - SPA 라 데이터 모델 값이 문서가 다시 로드될 때까지 남는다. 같은 값은 다시 올리지 않고(요청서 "1회만"),
 *   로그아웃으로 값이 사라지면 비운다. 비우지 않으면 다음 사람의 이벤트가 앞사람 것으로 잡힌다.
 *
 * @param {String|null} loginIdHash 로그인 상태가 아니면 null
 */
export function sendUserInfo(loginIdHash) {
    const hash = loginIdHash || null
    if (hash === lastLoginIdHash) return
    lastLoginIdHash = hash

    // 비울 때는 null 을 넣는다. GTM 데이터 모델은 객체를 합치기 때문에 null 로 덮어야 지워진다.
    push({ userInfo: hash ? { loginId: hash } : null })
}

const PURCHASE_SENT_KEY = "gtmPurchaseSent"
const PURCHASE_SENT_MAX = 20

/**
 * 멤버십 결제 완료를 올린다.
 *
 * 결제 완료 화면은 새로고침하면 다시 그려진다. 같은 결제를 두 번 올리지 않도록
 * 올린 paymentId 를 기억해 둔다(최근 몇 건만).
 *
 * 값이 없어도 키는 빼지 않고 null 로 싣는다. GTM 데이터 모델은 객체를 합치므로,
 * 키를 빼면 같은 문서에서 앞서 올린 결제의 값이 그대로 남아 읽힌다.
 *
 * @param {Object} payment
 * @param {String} payment.paymentId      중복 방지용. GA 로는 보내지 않는다.
 * @param {String} payment.membershipName 멤버십명
 * @param {String} payment.membershipNo   발급된 멤버십번호 (Rewards 등록 결과)
 * @param {String} payment.orderNo        주문번호 (shopOrderNo, 결제완료 화면의 "주문 번호")
 * @param {Number} payment.value          결제 금액(원)
 */
export function sendPurchase({ paymentId, membershipName, membershipNo, orderNo, value }) {
    let sent = []
    try {
        sent = JSON.parse(localStorage.getItem(PURCHASE_SENT_KEY)) || []
    } catch (e) {
        sent = []
    }
    if (sent.includes(paymentId)) return

    push({
        event: "purchase",
        purchaseInfo: {
            membershipName: membershipName ?? null,
            membershipNo: membershipNo ?? null,
            orderNo: orderNo ?? null,
            value: value ?? null,
        },
    })

    try {
        localStorage.setItem(PURCHASE_SENT_KEY, JSON.stringify([...sent, paymentId].slice(-PURCHASE_SENT_MAX)))
    } catch (e) {
        // 저장하지 못하면 새로고침 시 한 번 더 올라갈 수 있을 뿐이다.
    }
}
